import { border, lodestarColors, space, typography, ursaColors } from "@smartsheet/design-tokens";
import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { themed } from "@smartsheet/sds-theme-internal";
import { toRgba } from "../../../../constants/colors";
import { shadows } from "../../../../constants/layers";
import { sizes } from "../../../../constants/spacing";
import { DatePickerDayBoxProps } from "./DatePickerDayBox";

const {
    neutral,
    neutralLight10,
    neutralLight20,
    neutralLight40,
    neutralDark20,
    neutralDark30,
    neutralDark40,
    buttonDefault,
    focusLight10,
    visBlueLight30,
} = lodestarColors;
const { blue25, blue50, blue75, neutral60, neutral75, neutral400, neutral800, blue500, blue600 } = ursaColors;

export const datePickerTheme = themed({
    backgroundColor: color("color--background--surfaces--work", neutralLight40),
    boxShadow: {
        "lodestar-light": shadows.context(),
        "ursa-light": color("elevation--default"),
    },
    borderRadius: {
        "lodestar-light": `${sizes.xxSmall}px`,
        "ursa-light": space.space20,
    },
    color: color("color--text--neutral--default", neutralDark40),
    dayContainerMarginTop: {
        "lodestar-light": "18px",
        "ursa-light": space.space30,
    },
    footer: {
        marginTop: {
            "lodestar-light": space.space60,
            "ursa-light": space.space30,
        },
        buttonAppearance: {
            apply: {
                "lodestar-light": "primary",
                "ursa-light": "secondary-filled",
            },
            standard: {
                "lodestar-light": "secondary",
                "ursa-light": "secondary-filled",
            },
        } as const,
    },
    dateRangePickerInputTextSeparatorColor: {
        box: {
            "lodestar-light": color("color--text--neutral--strong", neutralDark30),
            "ursa-light": color("color--text--neutral--default", neutral800),
        },
        underline: color("color--text--neutral--strong", neutral800),
    },
    dayBackgroundColorActive: {
        "lodestar-light": color("color--background--neutral--default", neutralLight10),
        "ursa-light": color("color--background--neutral--default", neutral75),
    },
    dayBackgroundColorFocused: {
        "lodestar-light": buttonDefault, //no token exists
        "ursa-light": "transparent",
    },
    dayBackgroundColorFocusedSelecting: {
        "lodestar-light": buttonDefault, //no token exists
        "ursa-light": color("color--background--primary--weakest", blue50),
    },
    dayBackgroundColorHover: {
        "lodestar-light": buttonDefault, //no token exists
        "ursa-light": color("color--background--neutral--weak", neutral60),
    },
    dayBackgroundColorSelected: {
        "lodestar-light": focusLight10, //no token exists
        "ursa-light": color("color--background--primary--strong", blue500),
    },
    dayBackgroundColorToday: {
        "lodestar-light": "transparent",
        "ursa-light": color("color--background--primary--weakest", blue25),
    },
    dayBackgroundColorTodayActive: {
        "lodestar-light": buttonDefault, //no token exists
        "ursa-light": color("color--background--primary--weakest", blue50),
    },
    dayBackgroundColorTodayHover: {
        "lodestar-light": buttonDefault, //no token exists
        "ursa-light": color("color--background--primary--weakest", blue50),
    },
    dayBorderColorToday: {
        "lodestar-light": toRgba("#000000", 0.4), //no token exists
        "ursa-light": "transparent",
    },
    dayBorderRadius: {
        "lodestar-light": "2px",
        "ursa-light": border.radius20,
    },
    dayColor: {
        "lodestar-light": color("color--text--neutral--default", neutralDark30),
        "ursa-light": color("color--text--neutral--strong", neutral800),
    },
    dayColorDisabled: {
        "lodestar-light": color("color--background--neutral--strong", neutral),
        "ursa-light": color("color--text--neutral--weaker", "#71768A"), //no ursa fallback exists
    },
    dayColorSelected: {
        "lodestar-light": color("color--text--neutral--default", neutralDark30),
        "ursa-light": color("color--text--primary--weakest", blue25),
    },
    dayColorToday: {
        "lodestar-light": color("color--text--neutral--default", neutralDark30),
        "ursa-light": color("color--text--primary--default", blue600),
    },
    dayFocusRingGap: {
        "lodestar-light": 2,
        "ursa-light": 0,
    },
    dayFontWeightSelected: {
        "lodestar-light": 600,
        "ursa-light": typography.fontWeight.bold,
    },
    dayFontWeightToday: {
        "lodestar-light": 600,
        "ursa-light": typography.fontWeight.bold,
    },
    dayLabelColor: {
        "lodestar-light": color("color--text--neutral--weak", neutralDark20),
        "ursa-light": color("color--text--neutral--weaker", neutral400),
    },
    dayLabelFontSize: {
        "lodestar-light": "14px",
        "ursa-light": typography.fontSize.size20,
    },
    dayMargin: {
        "lodestar-light": "0",
        "ursa-light": `0 ${space.space20}`,
    },
    dayRowGap: {
        "lodestar-light": "0",
        "ursa-light": space.space10,
    },
    dayTransitionDuration: {
        "ursa-light": "75ms",
        "lodestar-light": "0s",
    },
    padding: {
        "lodestar-light": `${sizes.large}px 28px`,
        "ursa-light": space.space40,
    },
    topButtonType: {
        "lodestar-light": "borderless" as const,
        "ursa-light": "secondary-empty" as const,
    },
    dayWidth: {
        "lodestar-light": "40px",
        "ursa-light": space.space60,
    },
    dayInnerPadding: {
        "lodestar-light": "0",
        "ursa-light": "2px",
    },
    dateRangeLimitBorderFlatteningType: {
        "lodestar-light": "button",
        "ursa-light": "background",
    },
    dateRangePickerInRangeBackgroundColor: {
        inProgress: {
            "lodestar-light": buttonDefault, //no token exists
            "ursa-light": color("color--background--primary--weaker", blue75),
        },
        selected: {
            "lodestar-light": color("color--background--primary--weakest", visBlueLight30),
            "ursa-light": color("color--background--primary--weakest", blue50),
        },
    },
    menuOffset: {
        "lodestar-light": 1,
        "ursa-light": 2,
    },
    yearSelector: {
        backgroundColor: {
            hover: {
                "lodestar-light": color("color--background--neutral--weak", neutralLight20),
                "ursa-light": color("color--background--neutral--weaker", neutral60),
            },
            expanded: {
                "lodestar-light": color("color--background--neutral--default", neutralLight10),
                "ursa-light": color("color--background--neutral--default", neutral75),
            },
        },
        minheight: {
            "lodestar-light": "32px",
            "ursa-light": "none",
        },
        iconHeight: {
            "lodestar-light": "24px",
            "ursa-light": "none",
        },
    },
});

export function getTypeRampPropsMonthYear(themeMode: ThemeKey) {
    if (themeMode.includes("ursa")) {
        return {
            system: "ursa",
            variant: "Inter--16px--700w",
        } as const;
    }
    return {
        system: "lodestar",
        variant: "headlineSmall",
    } as const;
}

export function getTypeRampPropsDayOfWeek(themeMode: ThemeKey) {
    if (themeMode.includes("ursa")) {
        return {
            system: "ursa",
            variant: "Inter--12px--500w",
        } as const;
    }
    return {
        system: "lodestar",
        variant: "headerSmall",
    } as const;
}

export function getTypeRampPropsDay(themeMode: ThemeKey) {
    if (themeMode.includes("ursa")) {
        return {
            system: "ursa",
            variant: "Inter--14px--400w",
        } as const;
    }
    return {
        system: "lodestar",
        variant: "headerLarge",
    } as const;
}

// implements faux-rounded corners for days adjacent to invalid days, for ursa theme
export function getDisabledBumperStyles({ themeMode }: DatePickerDayBoxProps) {
    if (themeMode !== "ursa-light") return "";
    return `
        &:before,
        &:after {
            content: "";
            width: 10px;
            background-color: ${datePickerTheme.backgroundColor(themeMode)};
            height: 32px;
            position: absolute;
            top: -2px;
            mask-repeat: no-repeat;
            mask-size: contain;
            z-index: 1;
            pointer-events: none;
        }
        &:before {
            left: -14px;
            mask-image: url("data:image/svg+xml;base64,PHN2ZwogICB3aWR0aD0iMTAiCiAgIGhlaWdodD0iMzIiCiAgIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHBhdGgKICAgICBkPSJNIDAsMCBDIDIuMjA5MTM5LDAgNC4yMDg1MzQ4LDAuODk2MDM0NyA1LjY1NjI1LDIuMzQzNzUgNy4xMDM5NjUzLDMuNzkxNDY1MiA4LDUuNzkwODYxIDgsOCBWIDI0IEMgOCwyNi4yMDkxMzkgNy4xMDM5NjUzLDI4LjIwODUzNSA1LjY1NjI1LDI5LjY1NjI1IDQuMjA4NTM0OCwzMS4xMDM5NjUgMi4yMDkxMzksMzIgMCwzMiBjIDMuMzMzMzMzMywwIDYuNjY2NjY2NywwIDEwLDAgViAwIEMgNi4zNDY1MzY3LDAgMy41NTA2NTU0LDAgMCwwIFoiIC8+Cjwvc3ZnPgo=");
        }
        &:after {
            right: -14px;
            mask-image: url("data:image/svg+xml;base64,PHN2ZwogICB3aWR0aD0iMTAiCiAgIGhlaWdodD0iMzIiCiAgIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHBhdGgKICAgICBkPSJNIDEwLDAgQyA3Ljc5MDg2MSwwIDUuNzkxNDY1MiwwLjg5NjAzNDcgNC4zNDM3NSwyLjM0Mzc1IDIuODk2MDM0NywzLjc5MTQ2NTIgMiw1Ljc5MDg2MSAyLDggdiAxNiBjIDAsMi4yMDkxMzkgMC44OTYwMzQ3LDQuMjA4NTM1IDIuMzQzNzUsNS42NTYyNSBDIDUuNzkxNDY1MiwzMS4xMDM5NjUgNy43OTA4NjEsMzIgMTAsMzIgNi42NjY2NjY3LDMyIDMuMzMzMzMzMywzMiAwLDMyIFYgMCBjIDMuNjUzNDYzMywwIDYuNDQ5MzQ0NiwwIDEwLDAgeiIgLz4KPC9zdmc+Cg==");
        }
    `;
}
